import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    makeStyles,
    useTheme,
} from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApDropdown,
    ApDropdownItem,
    ApIconButton,
    ApTooltip,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useMemo,
} from 'react';
import { useIntl } from 'react-intl';

import type { ByoOperationGroupConfiguration } from '../../../../services/llm-gateway/LlmGateway.types';
import {
    getAlternativeLabelTranslationId,
    getAlternativeTooltipTranslationId,
    getModelConfiguration,
    isJarvisNaturalLanguageQueryFeature,
} from '../../../../services/llm-gateway/LlmGateway.utils';

interface ModelAlternativeDropdownProps {
    targetProduct: string;
    targetFeature: string;
    targetModel: string;
    value?: string;
    disabled?: boolean;
    alternatives?: string[]; // Optional override for alternatives
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>>; // Required productConfigurations
    onChange?: (value: string) => void;
}

const useStyles = makeStyles(_ =>
    createStyles({
        tooltipIcon: {
            width: '18px',
            height: '18px',
        },
    }),
);

export const ModelAlternativeDropdown: React.FC<ModelAlternativeDropdownProps> = ({
    targetProduct,
    targetFeature,
    targetModel,
    value,
    disabled,
    alternatives: overrideAlternatives,
    productConfigurations,
    onChange,
}) => {
    const { formatMessage: translate } = useIntl();
    const theme = useTheme();
    const classes = useStyles();

    const availableOptions = useMemo(() => {
        if (overrideAlternatives) {
            return [ targetModel, ...overrideAlternatives ];
        }

        const modelConfig = getModelConfiguration(productConfigurations, targetProduct, targetFeature, targetModel);
        const alternativesV2 = modelConfig?.alternativesWithProbes?.map(alt => alt.model ?? alt);
        const alternatives = modelConfig?.alternatives ?? alternativesV2 ?? [];

        // Include target model + alternatives, remove duplicates
        const allOptions = [ targetModel, ...alternatives ];
        return [ ...new Set(allOptions) ];
    }, [ overrideAlternatives, productConfigurations, targetProduct, targetFeature, targetModel ]);

    const getDropdownLabel = useCallback(() => {
        const id = getAlternativeLabelTranslationId(targetProduct, targetFeature, targetModel);
        return translate({ id });
    }, [ targetProduct, targetFeature, targetModel, translate ]);

    const handleOnChange = useCallback((event: CustomEvent) => {
        const newValue = event.detail as string;
        onChange?.(newValue);
    }, [ onChange ]);

    // Don't render if no options are available or only the target model exists
    if (!availableOptions.length || availableOptions.length === 1) {
        return null;
    }

    const isJarvisNaturalLanguageQuery = isJarvisNaturalLanguageQueryFeature(targetProduct, targetFeature);

    const labelText = getDropdownLabel();

    return (
        <div>
            {isJarvisNaturalLanguageQuery && (
                <UiStack
                    direction='row'
                    align='center'
                    mb={SpacingToken.Micro}
                    data-cy="model-alternative-dropdown-label"
                >
                    <UiText
                        variant={FontVariantToken.fontSizeMBold}
                        color={theme.palette.semantic.colorForegroundDeEmp}
                    >
                        {labelText}
                    </UiText>
                    <ApTooltip
                        content={(() => {
                            const tooltipId = getAlternativeTooltipTranslationId(targetProduct, targetFeature, targetModel);
                            return tooltipId ? translate({ id: tooltipId }) : undefined;
                        })()}
                    >
                        <ApIconButton
                            aria-label={(() => {
                                const tooltipId = getAlternativeTooltipTranslationId(targetProduct, targetFeature, targetModel);
                                return tooltipId ? translate({ id: tooltipId }) : undefined;
                            })()}
                            data-cy="model-alternative-dropdown-tooltip"
                        >
                            <InfoOutlinedIcon
                                className={classes.tooltipIcon}
                                color="action"
                            />
                        </ApIconButton>
                    </ApTooltip>
                </UiStack>
            )}

            <ApDropdown
                style={{ width: '350px' }}
                label={isJarvisNaturalLanguageQuery ? undefined : labelText}
                aria-label={labelText}
                selectedValue={value ?? targetModel}
                onSelectedValueChanged={handleOnChange}
                disabled={disabled}
                data-cy="model-alternative-dropdown"
            >
                {availableOptions.map((option) => (
                    <ApDropdownItem
                        key={option}
                        value={option}
                        label={option}
                    />
                ))}
            </ApDropdown>
        </div>
    );
};
